// ============================================================
// TMR Portal Admin — leituras e escritas da carteira de taxas
// ============================================================
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseQueryResult } from '@tanstack/react-query';
import { api } from '@tmr/api-client';
import { companyKeys, ratePortfolioKeys } from '../queryClient';
import type { RatesOutOfSyncPage, ReconcilePageResult } from './contract';

/**
 * One page of the Empresas whose rates disagree with the legacy.
 *
 * Never served from cache: it is a live comparison against a database with writers of its own — a coupon,
 * the ScriptCase back-office, an export that finished between two visits. A cached answer here would be a
 * statement about money that quietly aged.
 *
 * @param after Cursor from the previous page, or undefined to start at the beginning.
 * @param enabled Whether the operator holds `company.rates.admin` — false skips a request that would 403.
 */
export function useRatesOutOfSync(
  after: string | undefined,
  enabled: boolean,
): UseQueryResult<RatesOutOfSyncPage> {
  return useQuery({
    queryKey: ratePortfolioKeys.outOfSync(after ?? ''),
    queryFn: ({ signal }) => {
      const search = new URLSearchParams();
      if (after) search.set('after', after);

      const query = search.toString();
      return api.get<RatesOutOfSyncPage>(
        `/rates/out-of-sync${query ? `?${query}` : ''}`,
        { signal },
      );
    },
    enabled,
    staleTime: 0,
    gcTime: 0,
  });
}

/**
 * Reconciles one page of Empresas against the legacy.
 *
 * One page per call, by design on the server: a pass is a legacy round trip per Empresa, so walking the
 * whole base in a request would be a request that times out. The screen calls it repeatedly, carrying the
 * cursor, and can stop between pages — which is why the walk lives in the screen rather than here.
 */
export function useReconcilePage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (after: string | null) =>
      api.post<ReconcilePageResult>(
        `/rates/reconcile${after ? `?after=${encodeURIComponent(after)}` : ''}`,
        {},
      ),
    onSuccess: () => {
      // Both roots: the portfolio view is what this screen shows, and any Empresa tab left open is now
      // looking at rates this pass may have rewritten.
      void queryClient.invalidateQueries({ queryKey: ratePortfolioKeys.all });
      void queryClient.invalidateQueries({ queryKey: companyKeys.all });
    },
  });
}
